import { FeatureFlagsContext } from '@/common/featureFlagsWrapper';
import { MoreVert } from '@mui/icons-material';
import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { TablePaginationActions } from './TablePaginationActions';
import {
  ISpecialization,
  IUnderwriterWithSpecialization,
  SpecializationsData,
  TypeLookupEntity,
} from './types';

interface UnderwriterTableProps {
  underwriters: IUnderwriterWithSpecialization[];
  onEditUnderwriter: (underwriter: IUnderwriterWithSpecialization) => void;
  specializations: SpecializationsData;
}

interface SpecializationCellProps {
  specializations?: TypeLookupEntity[];
}

const SpecializationCell: React.FC<SpecializationCellProps> = ({
  specializations,
}) => {
  if (!specializations?.length)
    return <Typography color="textSecondary">-</Typography>;

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ py: 0.5 }}>
      {specializations.map(spec => (
        <Chip
          key={spec.id}
          label={spec.displayText}
          size="small"
          color="default"
          variant="outlined"
          sx={{
            borderRadius: 1.5,
          }}
        />
      ))}
    </Stack>
  );
};

export function UnderwriterTable({
  underwriters,
  onEditUnderwriter,
  specializations,
}: UnderwriterTableProps) {
  const { flags } = useContext(FeatureFlagsContext);
  const isMaxCoverageEditEnabled = flags.isSpecializationMaxCoverageEditEnabled === true;
  const isAuthorityLimitEditEnabled = flags.isSpecializationAuthorityLimitEditEnabled === true;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    event?.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getSpecializationsByType = (
    uwSpecializations: ISpecialization[],
    type: string,
  ) => {
    if (type === 'PRODUCT') {
      return specializations.product.filter(s =>
        uwSpecializations.some(
          uw => uw.specialization.name.toLowerCase() === s.name.toLowerCase(),
        ),
      );
    }
    if (type === 'LIFECYCLE') {
      return specializations.lifecycle.filter(s =>
        uwSpecializations.some(
          uw => uw.specialization.name.toLowerCase() === s.name.toLowerCase(),
        ),
      );
    }
    // Match by type + name: MAX_COVERAGE and AUTHORITY_LIMIT share the same name
    // values (e.g. "2000000"), so name-only matching would show the wrong chip.
    if (type === 'MAX_COVERAGE') {
      return specializations.maxCoverage.filter(s =>
        uwSpecializations.some(
          uw =>
            uw.specialization.type === 'MAX_COVERAGE' &&
            uw.specialization.name.toLowerCase() === s.name.toLowerCase(),
        ),
      );
    }
    // Shown when uw_authority_limit_assignment is on; see coverageColumnType below.
    if (type === 'AUTHORITY_LIMIT') {
      return (specializations.authorityLimit ?? []).filter(s =>
        uwSpecializations.some(
          uw =>
            uw.specialization.type === 'AUTHORITY_LIMIT' &&
            uw.specialization.name.toLowerCase() === s.name.toLowerCase(),
        ),
      );
    }
    if (type === 'SPECIALIZATION_FLAG') {
      return specializations.specialization.filter(s =>
        uwSpecializations.some(
          uw => uw.specialization.name.toLowerCase() === s.name.toLowerCase(),
        ),
      );
    }
  };

  // Calculate empty rows when needed
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - underwriters.length) : 0;

  // Name + Product + Lifecycle + coverage col(s) + optional spec flags + Actions
  const totalColumns =
    3 +
    (isMaxCoverageEditEnabled ? 1 : 0) +
    (isAuthorityLimitEditEnabled ? 1 : 0) +
    (specializations.specialization?.length > 0 ? 1 : 0) +
    1;

  return (
    <TableContainer
      sx={{ borderRadius: 2, border: '1px solid var(--neutrals-night-20)' }}
    >
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: 'var(--neutrals-truffle-15)',
              '& th': {
                fontWeight: 500,
              },
            }}
          >
            <TableCell>Name</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Lifecycle</TableCell>
            {isMaxCoverageEditEnabled && <TableCell>Max Coverage</TableCell>}
            {isAuthorityLimitEditEnabled && <TableCell>Authority Limit</TableCell>}
            {specializations.specialization?.length > 0 && (
              <TableCell>Specialization Flags</TableCell>
            )}
            <TableCell align="center" sx={{ width: 100 }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {underwriters.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalColumns} align="center">
                No underwriters found
              </TableCell>
            </TableRow>
          ) : (
            (rowsPerPage > 0
              ? underwriters.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage,
                )
              : underwriters
            ).map(underwriter => (
              <TableRow
                key={underwriter.id}
                sx={{
                  '&:hover': {
                    backgroundColor: theme => theme.palette.action.hover,
                  },
                  '& td': {
                    borderBottom: '1px solid var(--neutrals-night-20)',
                    py: 1.5,
                  },
                }}
              >
                <TableCell>
                  {underwriter.displayName
                    ? underwriter.displayName
                    : underwriter.firstName + ' ' + underwriter.lastName}
                </TableCell>
                <TableCell>
                  <SpecializationCell
                    specializations={getSpecializationsByType(
                      underwriter.specializations,
                      'PRODUCT',
                    )}
                  />
                </TableCell>
                <TableCell>
                  <SpecializationCell
                    specializations={getSpecializationsByType(
                      underwriter.specializations,
                      'LIFECYCLE',
                    )}
                  />
                </TableCell>
                {isMaxCoverageEditEnabled && (
                  <TableCell>
                    <SpecializationCell
                      specializations={getSpecializationsByType(
                        underwriter.specializations,
                        'MAX_COVERAGE',
                      )}
                    />
                  </TableCell>
                )}
                {isAuthorityLimitEditEnabled && (
                  <TableCell>
                    <SpecializationCell
                      specializations={getSpecializationsByType(
                        underwriter.specializations,
                        'AUTHORITY_LIMIT',
                      )}
                    />
                  </TableCell>
                )}
                {specializations.specialization?.length > 0 && (
                  <TableCell>
                    <SpecializationCell
                      specializations={getSpecializationsByType(
                        underwriter.specializations,
                        'SPECIALIZATION_FLAG',
                      )}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => onEditUnderwriter(underwriter)}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={totalColumns} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              colSpan={totalColumns}
              count={underwriters.length}
              rowsPerPage={rowsPerPage}
              page={page}
              slotProps={{
                select: {
                  inputProps: {
                    'aria-label': 'rows per page',
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
